import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Camera, Loader2, Sparkles } from "lucide-react";
import {
  runPropertySummary,
  readExistingSummary,
} from "../services/propertySummaryService";
import { toPropertySummary } from "./propertySummary";

/**
 * The AI summary of a property's photographs.
 *
 * The backend runs it and keeps it, so this asks for an existing one on load —
 * a single cheap GET — and shows it if there is one. Generating is behind a
 * button because it takes minutes and the model's answer would only change by
 * chance on a re-run.
 *
 * No client-side cache: the backend is the store now. The earlier version
 * cached in sessionStorage because the standalone service kept results in
 * memory for an hour and lost them on restart.
 */

const card = "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
const cardHead =
  "flex items-center justify-between gap-3 border-b border-cl-head px-4 py-[11px]";
const cardTitle = "text-[13.5px] font-semibold text-cl-text";
const labelClass =
  "text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label";

const TONES = {
  good: { bg: "#e3f4e8", fg: "#187a3c" },
  warn: { bg: "#fdf2dc", fg: "#9a6a10" },
  bad: { bg: "#fbe9e9", fg: "#c22f2f" },
  muted: { bg: "var(--cl-app-bg)", fg: "var(--cl-text-muted)" },
};

const SEVERITY_TONE = { severe: "bad", moderate: "warn", minor: "muted" };

const STAGE_LABELS = {
  checking: "Checking for an existing summary",
  starting: "Asking the backend to run it",
  running: "Reading the photographs",
};

function Chip({ tone = "muted", children }) {
  const { bg, fg } = TONES[tone] || TONES.muted;
  return (
    <span
      className="flex-none rounded-pill px-[9px] py-[2px] text-[10.5px] font-bold uppercase tracking-[0.03em]"
      style={{ background: bg, color: fg }}
    >
      {children}
    </span>
  );
}

/** A finding, with the photographs it was seen in. */
function Finding({ title, tone, detail, evidence, onOpenPhoto }) {
  return (
    <div className="border-b border-cl-row px-4 py-[11px] last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] font-semibold text-cl-text">{title}</span>
        {tone && <Chip tone={tone.tone}>{tone.label}</Chip>}
      </div>
      {detail && (
        <p className="mt-[3px] text-[12px] leading-[1.5] text-cl-secondary">
          {detail}
        </p>
      )}
      {evidence?.length > 0 && (
        <div className="mt-[7px] flex flex-wrap gap-[6px]">
          {evidence.map((photo) => (
            <button
              key={photo.id}
              type="button"
              onClick={() => onOpenPhoto?.(photo)}
              title={`Seen in ${photo.label}`}
              className="flex items-center gap-[5px] rounded-pill bg-cl-bg px-[9px] py-[3px] text-[10.5px] font-medium text-cl-secondary transition-colors hover:bg-cl-hover"
            >
              <Camera className="h-[10px] w-[10px] text-cl-faint" />
              {photo.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PropertySummaryPanel({ detail, onOpenPhoto }) {
  const refNum = detail?.refNum;
  /**
   * The backend indexes evidence_images against its own photograph list, which
   * is what property_data is — so these resolve findings back to frames.
   *
   * Memoised because `|| []` yields a new array every render, which would make
   * the generate callback unstable and refire the effects that depend on it.
   */
  const photos = useMemo(() => detail?.photos || [], [detail?.photos]);
  const [state, setState] = useState({
    status: "loading",
    progress: null,
    summary: null,
    error: null,
  });
  const abortRef = useRef(null);

  // One GET on load: the backend keeps the summary, so a case that has already
  // been summarised should show it without being asked.
  useEffect(() => {
    if (!refNum) return undefined;
    let cancelled = false;

    setState({ status: "loading", progress: null, summary: null, error: null });
    readExistingSummary(refNum)
      .then((result) => {
        if (cancelled) return;
        setState({
          status: result ? "done" : "idle",
          progress: null,
          summary: result ? toPropertySummary(result, photos) : null,
          error: null,
        });
      })
      .catch(() => {
        // A failed lookup is not a failed summary — offer to generate one.
        if (!cancelled) {
          setState({ status: "idle", progress: null, summary: null, error: null });
        }
      });

    return () => {
      cancelled = true;
    };
    // Keyed on the case: re-running when the photo array is re-created would
    // refetch on every render of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refNum]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const generate = useCallback(
    async ({ force = false } = {}) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setState((current) => ({
        ...current,
        status: "running",
        error: null,
        progress: { stage: force ? "starting" : "checking" },
      }));

      try {
        const result = await runPropertySummary(refNum, {
          force,
          signal: controller.signal,
          onProgress: (progress) =>
            setState((current) => ({ ...current, progress })),
        });

        setState({
          status: "done",
          progress: null,
          summary: toPropertySummary(result, photos),
          error: null,
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
        setState((current) => ({
          ...current,
          status: "error",
          progress: null,
          error: error?.message || "The summary could not be generated",
        }));
      }
    },
    [refNum, photos],
  );

  const { status, progress, summary, error } = state;

  return (
    <div className={card}>
      <div className={cardHead}>
        <div className="flex items-center gap-2">
          <Sparkles className="h-[15px] w-[15px] text-cl-accent" />
          <span className={cardTitle}>Property summary</span>
        </div>
        {status === "done" && summary?.region?.name && (
          <span className="text-[11px] text-cl-faint">
            {summary.region.name} checklist
          </span>
        )}
      </div>

      {/* Looking for one the backend already has */}
      {status === "loading" && (
        <div className="flex items-center gap-3 px-4 py-[18px]">
          <Loader2 className="h-4 w-4 animate-spin text-cl-faint" />
          <span className="text-[12.5px] text-cl-muted">
            Checking for a summary…
          </span>
        </div>
      )}

      {/* Idle */}
      {status === "idle" && (
        <div className="px-4 py-[15px]">
          <p className="text-[12.5px] leading-[1.55] text-cl-secondary">
            Reads all {photos.length} photograph
            {photos.length === 1 ? "" : "s"} together and returns one
            assessment — construction, condition, visible damage, risks and a
            region checklist.
          </p>
          <button
            type="button"
            onClick={() => generate({ force: false })}
            disabled={photos.length === 0}
            className={`mt-3 rounded-tile px-[13px] py-[7px] text-[12.5px] font-semibold transition-colors ${
              photos.length
                ? "bg-cl-text text-white hover:opacity-90"
                : "cursor-not-allowed bg-cl-bg text-cl-disabled"
            }`}
          >
            {photos.length ? "Generate summary" : "No photographs to read"}
          </button>
          <p className="mt-[9px] text-[11px] text-cl-faint">
            Takes 30 seconds to 3 minutes.
          </p>
        </div>
      )}

      {/* Running */}
      {status === "running" && (
        <div className="flex items-center gap-3 px-4 py-[18px]">
          <Loader2 className="h-4 w-4 animate-spin text-cl-accent" />
          <div className="min-w-0">
            <div className="text-[12.5px] font-medium text-cl-text">
              {STAGE_LABELS[progress?.stage] || "Working"}
            </div>
            {progress?.reason && (
              <div className="mt-[2px] text-[11.5px] text-cl-muted">
                {progress.reason}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Failed */}
      {status === "error" && (
        <div className="px-4 py-[15px]">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-[2px] h-[13px] w-[13px] flex-none text-[#9a6212]" />
            <p className="text-[12.5px] leading-[1.5] text-cl-secondary">
              {error}
            </p>
          </div>
          <p className="mt-2 text-[11px] leading-[1.5] text-cl-faint">
            The summary runs on the property backend, on the same host and token
            as the rest of this case.
          </p>
          <button
            type="button"
            onClick={() => generate({ force: false })}
            className="mt-3 rounded-tile border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
          >
            Try again
          </button>
        </div>
      )}

      {/* Done */}
      {status === "done" && summary && (
        <>
          <div className="border-b border-cl-head px-4 py-[13px]">
            <div className="flex flex-wrap items-center gap-2">
              {summary.propertyType && (
                <span className="text-[14.5px] font-bold text-cl-text">
                  {summary.propertyType}
                </span>
              )}
              {summary.condition.label && (
                <Chip tone={summary.condition.tone}>
                  {summary.condition.label}
                </Chip>
              )}
            </div>
            {summary.condition.explanation && (
              <p className="mt-[4px] text-[12px] leading-[1.5] text-cl-secondary">
                {summary.condition.explanation}
              </p>
            )}
            {summary.narrative && (
              <p className="mt-[9px] text-[12.5px] leading-[1.6] text-cl-text">
                {summary.narrative}
              </p>
            )}
            {summary.imageCount > 0 && summary.imageCount < photos.length && (
              <p className="mt-[9px] text-[11px] text-cl-faint">
                Read {summary.imageCount} of {photos.length} photographs.
              </p>
            )}
          </div>

          {summary.damages.length > 0 && (
            <div className="border-b border-cl-head">
              <div className="px-4 pb-[6px] pt-[11px]">
                <span className={labelClass}>
                  Visible damage · {summary.damages.length}
                </span>
              </div>
              {summary.damages.map((damage) => (
                <Finding
                  key={damage.id}
                  title={damage.element}
                  tone={
                    damage.severityLabel && {
                      tone: SEVERITY_TONE[damage.severity] || "muted",
                      label: damage.severityLabel,
                    }
                  }
                  detail={damage.description}
                  evidence={damage.evidence}
                  onOpenPhoto={onOpenPhoto}
                />
              ))}
            </div>
          )}

          {/* Only what the model actually judged. A system it never saw is a
              request for a photograph, not a finding — grouped below. */}
          {summary.systems.some((system) => system.assessed) && (
            <div className="border-b border-cl-head">
              <div className="px-4 pb-[6px] pt-[11px]">
                <span className={labelClass}>Systems</span>
              </div>
              {summary.systems
                .filter((system) => system.assessed)
                .map((system) => (
                  <Finding
                    key={system.key}
                    title={system.label}
                    tone={
                      system.condition && {
                        tone: SEVERITY_TONE[system.severity] || "muted",
                        label: system.condition,
                      }
                    }
                    detail={system.observations}
                    evidence={system.evidence}
                    onOpenPhoto={onOpenPhoto}
                  />
                ))}
            </div>
          )}

          {summary.checklist.length > 0 && (
            <div className="border-b border-cl-head">
              <div className="px-4 pb-[6px] pt-[11px]">
                <span className={labelClass}>
                  {summary.region.name || "Region"} checklist
                </span>
              </div>
              <div className="px-4 pb-[11px]">
                {summary.checklist.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-[9px] border-b border-cl-row py-[8px] last:border-b-0"
                  >
                    <span
                      className="mt-[5px] h-[8px] w-[8px] flex-none rounded-[3px]"
                      style={{
                        background: !item.assessed
                          ? "var(--cl-border-control)"
                          : item.present
                            ? "var(--cl-approve)"
                            : "var(--cl-accent)",
                      }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-[6px]">
                        <span className="text-[12.5px] font-medium text-cl-text">
                          {item.label}
                        </span>
                        {item.high && <Chip tone="warn">High priority</Chip>}
                      </div>
                      <div className="mt-[1px] text-[11.5px] text-cl-muted">
                        {!item.assessed
                          ? "Not visible in the photographs"
                          : item.present
                            ? "Present"
                            : "Not present"}
                        {item.notes ? ` · ${item.notes}` : ""}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(summary.risks.length > 0 || summary.hazards.length > 0) && (
            <div className="border-b border-cl-head">
              <div className="px-4 pb-[6px] pt-[11px]">
                <span className={labelClass}>Risks</span>
              </div>
              {summary.hazards.map((hazard) => (
                <Finding
                  key={hazard.id}
                  title={hazard.hazard}
                  detail={hazard.description}
                  evidence={hazard.evidence}
                  onOpenPhoto={onOpenPhoto}
                />
              ))}
              {summary.risks.length > 0 && (
                <div className="flex flex-wrap gap-[6px] px-4 py-[11px]">
                  {summary.risks.map((risk) => (
                    <span
                      key={risk.id}
                      className="rounded-pill bg-cl-bg px-[10px] py-[3px] text-[11.5px] text-cl-secondary"
                    >
                      {risk.factor}
                      {risk.category ? ` · ${risk.category}` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {summary.recommendations.length > 0 && (
            <div className="border-b border-cl-head px-4 py-[11px]">
              <span className={labelClass}>Recommended</span>
              <ul className="mt-[6px] list-disc pl-[18px] text-[12.5px] leading-[1.6] text-cl-secondary">
                {summary.recommendations.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* What was not photographed. Actionable in a way the rest is not:
              it is a list of shots to ask the surveyor for. */}
          {(summary.coverage.notCovered.length > 0 ||
            summary.systems.some((system) => !system.assessed)) && (
            <div className="border-b border-cl-head px-4 py-[11px]">
              <span className={labelClass}>Not photographed</span>
              <p className="mt-[5px] text-[12px] leading-[1.55] text-cl-secondary">
                {[
                  ...summary.coverage.notCovered,
                  ...summary.systems
                    .filter((system) => !system.assessed)
                    .map((system) => system.label.toLowerCase()),
                ].join(", ")}
              </p>
              <p className="mt-[5px] text-[11px] leading-[1.5] text-cl-faint">
                No evidence either way — not a finding that these are absent.
              </p>
            </div>
          )}

          {summary.limitations.length > 0 && (
            <p className="bg-[#fdf6e7] px-4 py-[10px] text-[11.5px] leading-[1.5] text-[#7a5a12]">
              {summary.limitations.join(" · ")}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 px-4 py-[10px]">
            <span className="text-[11px] text-cl-faint">
              Read by an 8B vision model — a first-pass screen, not a survey.
            </span>
            <button
              type="button"
              onClick={() => generate({ force: true })}
              className="flex-none rounded-tile border border-cl-control px-[11px] py-[5px] text-[11.5px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
            >
              Run again
            </button>
          </div>
        </>
      )}
    </div>
  );
}
